import React, { useState, useEffect, useRef } from 'react';
import { 
  FileText, 
  BarChart3, 
  Download, 
  Filter,
  DollarSign,
  Bed,
  Utensils,
  Calendar,
  Target,
  TrendingUp,
  RefreshCw,
  ChevronDown,
  IndianRupee
} from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface RevenueData {
  month: string;
  roomRevenue: number;
  foodRevenue: number;
  otherRevenue: number;
  totalRevenue: number;
}

const ReportingCenter: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedReport, setSelectedReport] = useState('revenue');
  const [selectedProperty, setSelectedProperty] = useState('all-properties');
  const [isLoading, setIsLoading] = useState(false);
  const [isPeriodDropdownOpen, setIsPeriodDropdownOpen] = useState(false);
  const [isReportDropdownOpen, setIsReportDropdownOpen] = useState(false);
  const [isPropertyDropdownOpen, setIsPropertyDropdownOpen] = useState(false);
  const periodDropdownRef = useRef<HTMLDivElement>(null);
  const reportDropdownRef = useRef<HTMLDivElement>(null);
  const propertyDropdownRef = useRef<HTMLDivElement>(null);

  // Different data sets for different periods, reports, and properties
  const getFinancialData = (period: string, report: string, property: string) => {
    // Base multipliers for different properties
    const propertyMultipliers = {
      'all-properties': 1,
      'main-branch': 0.4,
      'downtown-branch': 0.3,
      'airport-branch': 0.2,
      'beach-resort': 0.1
    };

    const multiplier = propertyMultipliers[property as keyof typeof propertyMultipliers] || 1;

    // Revenue data
    const revenueDataSets = {
      week: {
        revenueData: [
          { day: 'Mon', roomRevenue: 45000, foodRevenue: 18000, otherRevenue: 5000, totalRevenue: 68000 },
          { day: 'Tue', roomRevenue: 52000, foodRevenue: 22000, otherRevenue: 6000, totalRevenue: 80000 },
          { day: 'Wed', roomRevenue: 48000, foodRevenue: 19000, otherRevenue: 5500, totalRevenue: 72500 },
          { day: 'Thu', roomRevenue: 55000, foodRevenue: 25000, otherRevenue: 7000, totalRevenue: 87000 },
          { day: 'Fri', roomRevenue: 62000, foodRevenue: 28000, otherRevenue: 8000, totalRevenue: 98000 },
          { day: 'Sat', roomRevenue: 68000, foodRevenue: 32000, otherRevenue: 9000, totalRevenue: 109000 },
          { day: 'Sun', roomRevenue: 45000, foodRevenue: 18000, otherRevenue: 5000, totalRevenue: 68000 }
        ]
      },
      month: {
        revenueData: [
          { week: 'Week 1', roomRevenue: 650000, foodRevenue: 240000, otherRevenue: 80000, totalRevenue: 970000 },
          { week: 'Week 2', roomRevenue: 720000, foodRevenue: 280000, otherRevenue: 90000, totalRevenue: 1090000 },
          { week: 'Week 3', roomRevenue: 680000, foodRevenue: 260000, otherRevenue: 85000, totalRevenue: 1025000 },
          { week: 'Week 4', roomRevenue: 750000, foodRevenue: 300000, otherRevenue: 95000, totalRevenue: 1145000 }
        ]
      },
      quarter: {
        revenueData: [
          { month: 'Month 1', roomRevenue: 2800000, foodRevenue: 1100000, otherRevenue: 350000, totalRevenue: 4250000 },
          { month: 'Month 2', roomRevenue: 2950000, foodRevenue: 1200000, otherRevenue: 380000, totalRevenue: 4530000 },
          { month: 'Month 3', roomRevenue: 3100000, foodRevenue: 1300000, otherRevenue: 400000, totalRevenue: 4800000 }
        ]
      },
      year: {
        revenueData: [
          { quarter: 'Q1', roomRevenue: 8500000, foodRevenue: 3200000, otherRevenue: 1000000, totalRevenue: 12700000 },
          { quarter: 'Q2', roomRevenue: 9200000, foodRevenue: 3600000, otherRevenue: 1100000, totalRevenue: 13900000 },
          { quarter: 'Q3', roomRevenue: 9800000, foodRevenue: 3800000, otherRevenue: 1200000, totalRevenue: 14800000 },
          { quarter: 'Q4', roomRevenue: 10500000, foodRevenue: 4000000, otherRevenue: 1300000, totalRevenue: 15800000 }
        ]
      }
    };

    // Occupancy data
    const occupancyDataSets = {
      week: {
        occupancyData: [
          { day: 'Mon', roomOccupancy: 75, foodOccupancy: 60, otherOccupancy: 40, totalOccupancy: 58 },
          { day: 'Tue', roomOccupancy: 85, foodOccupancy: 70, otherOccupancy: 50, totalOccupancy: 68 },
          { day: 'Wed', roomOccupancy: 80, foodOccupancy: 65, otherOccupancy: 45, totalOccupancy: 63 },
          { day: 'Thu', roomOccupancy: 90, foodOccupancy: 75, otherOccupancy: 55, totalOccupancy: 73 },
          { day: 'Fri', roomOccupancy: 95, foodOccupancy: 80, otherOccupancy: 60, totalOccupancy: 78 },
          { day: 'Sat', roomOccupancy: 100, foodOccupancy: 85, otherOccupancy: 65, totalOccupancy: 83 },
          { day: 'Sun', roomOccupancy: 70, foodOccupancy: 55, otherOccupancy: 35, totalOccupancy: 53 }
        ]
      },
      month: {
        occupancyData: [
          { week: 'Week 1', roomOccupancy: 82, foodOccupancy: 68, otherOccupancy: 48, totalOccupancy: 66 },
          { week: 'Week 2', roomOccupancy: 88, foodOccupancy: 72, otherOccupancy: 52, totalOccupancy: 71 },
          { week: 'Week 3', roomOccupancy: 85, foodOccupancy: 70, otherOccupancy: 50, totalOccupancy: 68 },
          { week: 'Week 4', roomOccupancy: 92, foodOccupancy: 76, otherOccupancy: 56, totalOccupancy: 75 }
        ]
      },
      quarter: {
        occupancyData: [
          { month: 'Month 1', roomOccupancy: 85, foodOccupancy: 70, otherOccupancy: 50, totalOccupancy: 68 },
          { month: 'Month 2', roomOccupancy: 88, foodOccupancy: 73, otherOccupancy: 53, totalOccupancy: 71 },
          { month: 'Month 3', roomOccupancy: 90, foodOccupancy: 75, otherOccupancy: 55, totalOccupancy: 73 }
        ]
      },
      year: {
        occupancyData: [
          { quarter: 'Q1', roomOccupancy: 87, foodOccupancy: 72, otherOccupancy: 52, totalOccupancy: 70 },
          { quarter: 'Q2', roomOccupancy: 89, foodOccupancy: 74, otherOccupancy: 54, totalOccupancy: 72 },
          { quarter: 'Q3', roomOccupancy: 91, foodOccupancy: 76, otherOccupancy: 56, totalOccupancy: 74 },
          { quarter: 'Q4', roomOccupancy: 93, foodOccupancy: 78, otherOccupancy: 58, totalOccupancy: 76 }
        ]
      }
    };

    if (report === 'occupancy') {
      const data = occupancyDataSets[period as keyof typeof occupancyDataSets] || occupancyDataSets.month;
      return {
        occupancyData: data.occupancyData.map(item => ({
          ...item,
          roomOccupancy: Math.round(item.roomOccupancy * multiplier),
          foodOccupancy: Math.round(item.foodOccupancy * multiplier),
          otherOccupancy: Math.round(item.otherOccupancy * multiplier),
          totalOccupancy: Math.round(item.totalOccupancy * multiplier)
        }))
      };
    } else {
      const data = revenueDataSets[period as keyof typeof revenueDataSets] || revenueDataSets.month;
      return {
        revenueData: data.revenueData.map(item => ({
          ...item,
          roomRevenue: Math.round(item.roomRevenue * multiplier),
          foodRevenue: Math.round(item.foodRevenue * multiplier),
          otherRevenue: Math.round(item.otherRevenue * multiplier),
          totalRevenue: Math.round(item.totalRevenue * multiplier)
        }))
      };
    }
  };

  const currentData = getFinancialData(selectedPeriod, selectedReport, selectedProperty);
  const revenueData = currentData.revenueData || [];
  const occupancyData = currentData.occupancyData || [];

  // Prepare data for Chart.js
  const chartData = selectedReport === 'occupancy' ? {
    labels: (occupancyData as any[]).map(data => 
      'day' in data ? data.day : 
      'week' in data ? data.week : 
      'month' in data ? data.month : 
      data.quarter
    ),
    datasets: [
      {
        label: 'Room Occupancy',
        data: (occupancyData as any[]).map(data => data.roomOccupancy),
        backgroundColor: '#D69524',
        borderColor: '#B8821F',
        borderWidth: 0,
        borderRadius: 0,
        borderSkipped: false,
      },
    ],
  } : {
    labels: (revenueData as any[]).map(data => 
      'day' in data ? data.day : 
      'week' in data ? data.week : 
      'month' in data ? data.month : 
      data.quarter
    ),
    datasets: [
      {
        label: 'Room Revenue',
        data: (revenueData as any[]).map(data => data.roomRevenue),
        backgroundColor: '#157280',
        borderColor: '#0F5A66',
        borderWidth: 0,
        borderRadius: 0,
        borderSkipped: false,
      },
      {
        label: 'F&B Revenue',
        data: (revenueData as any[]).map(data => data.foodRevenue),
        backgroundColor: '#D69524',
        borderColor: '#B8821F',
        borderWidth: 0,
        borderRadius: 0,
        borderSkipped: false,
      },
      {
        label: 'Banquet Revenue',
        data: (revenueData as any[]).map(data => data.otherRevenue),
        backgroundColor: '#802315',
        borderColor: '#6B1E12',
        borderWidth: 0,
        borderRadius: 0,
        borderSkipped: false,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 14,
            weight: 'bold' as const,
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: function(context: any) {
            return selectedReport === 'occupancy' 
              ? `${context.dataset.label}: ${context.parsed.y}%`
              : `${context.dataset.label}: ${formatCurrency(context.parsed.y)}`;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: selectedReport !== 'occupancy',
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 12,
            weight: 'bold' as const,
          },
          color: '#374151',
        },
      },
      y: {
        stacked: selectedReport !== 'occupancy',
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 11,
            weight: 'bold' as const,
          },
          color: '#6B7280',
          callback: function(value: any) {
            return selectedReport === 'occupancy' ? `${value}%` : formatCurrency(value);
          },
        },
      },
    },
  };

  const periodOptions = [
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'quarter', label: 'This Quarter' },
    { value: 'year', label: 'This Year' }
  ];

  const reportOptions = [
    { value: 'revenue', label: 'Revenue' },
    { value: 'occupancy', label: 'Occupancy' }
  ];

  const propertyOptions = [
    { value: 'all-properties', label: 'All Properties' },
    { value: 'main-branch', label: 'Main Branch' },
    { value: 'downtown-branch', label: 'Downtown Branch' },
    { value: 'airport-branch', label: 'Airport Branch' },
    { value: 'beach-resort', label: 'Beach Resort' }
  ];

  // Handle clicks outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (periodDropdownRef.current && !periodDropdownRef.current.contains(event.target as Node)) {
        setIsPeriodDropdownOpen(false);
      }
      if (reportDropdownRef.current && !reportDropdownRef.current.contains(event.target as Node)) {
        setIsReportDropdownOpen(false);
      }
      if (propertyDropdownRef.current && !propertyDropdownRef.current.contains(event.target as Node)) {
        setIsPropertyDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleRefresh = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };

  const handleDownloadReport = async () => {
    setIsLoading(true);
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const chartWidth = pageWidth - (margin * 2);
      const chartHeight = 80;

      // Add title
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Comprehensive Report', margin, 30);
      
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Report Type: ${selectedReport === 'occupancy' ? 'Room Occupancy' : 'Revenue Breakdown'}`, margin, 40);
      pdf.text(`Generated: ${new Date().toLocaleString()}`, margin, 50);

      let yPosition = 60;

      // Generate charts for all combinations
      for (const property of propertyOptions) {
        for (const period of periodOptions) {
          // Skip if we're at the end of the page
          if (yPosition + chartHeight > pageHeight - margin) {
            pdf.addPage();
            yPosition = 20;
          }

          // Get data for this combination
          const data = getFinancialData(period.value, selectedReport, property.value);
          const chartData = selectedReport === 'occupancy' ? {
            labels: (data.occupancyData || []).map((d: any) => 
              'day' in d ? d.day : 
              'week' in d ? d.week : 
              'month' in d ? d.month : 
              d.quarter
            ),
            datasets: [{
              label: 'Room Occupancy',
              data: (data.occupancyData || []).map((d: any) => d.roomOccupancy),
              backgroundColor: '#D69524',
              borderColor: '#B8821F',
              borderWidth: 0,
              borderRadius: 0,
              borderSkipped: false,
            }],
          } : {
            labels: (data.revenueData || []).map((d: any) => 
              'day' in d ? d.day : 
              'week' in d ? d.week : 
              'month' in d ? d.month : 
              d.quarter
            ),
            datasets: [
              {
                label: 'Room Revenue',
                data: (data.revenueData || []).map((d: any) => d.roomRevenue),
                backgroundColor: '#157280',
                borderColor: '#0F5A66',
                borderWidth: 0,
                borderRadius: 0,
                borderSkipped: false,
              },
              {
                label: 'F&B Revenue',
                data: (data.revenueData || []).map((d: any) => d.foodRevenue),
                backgroundColor: '#D69524',
                borderColor: '#B8821F',
                borderWidth: 0,
                borderRadius: 0,
                borderSkipped: false,
              },
              {
                label: 'Banquet Revenue',
                data: (data.revenueData || []).map((d: any) => d.otherRevenue),
                backgroundColor: '#802315',
                borderColor: '#6B1E12',
                borderWidth: 0,
                borderRadius: 0,
                borderSkipped: false,
              },
            ],
          };

          // Add section header
          pdf.setFontSize(14);
          pdf.setFont('helvetica', 'bold');
          pdf.text(`${property.label} - ${period.label}`, margin, yPosition);
          
          yPosition += 10;

          // Create a temporary div for the chart
          const tempDiv = document.createElement('div');
          tempDiv.style.width = `${chartWidth * 3.78}px`; // Convert mm to px
          tempDiv.style.height = `${chartHeight * 3.78}px`;
          tempDiv.style.position = 'absolute';
          tempDiv.style.left = '-9999px';
          tempDiv.style.top = '-9999px';
          tempDiv.style.backgroundColor = 'white';
          tempDiv.style.padding = '10px';
          tempDiv.style.borderRadius = '8px';
          tempDiv.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
          
          document.body.appendChild(tempDiv);

          // Create chart using Chart.js
          const canvas = document.createElement('canvas');
          canvas.width = chartWidth * 3.78;
          canvas.height = chartHeight * 3.78;
          tempDiv.appendChild(canvas);

          const ctx = canvas.getContext('2d');
          if (ctx) {
            // Simple bar chart drawing
            const dataArray = chartData.datasets[0].data;
            const maxValue = Math.max(...dataArray);
            const barWidth = (chartWidth * 3.78 - 40) / dataArray.length;
            const chartHeightPx = chartHeight * 3.78 - 40;

            // Draw bars
            dataArray.forEach((value, index) => {
              const barHeight = (value / maxValue) * chartHeightPx;
              const x = 20 + index * barWidth;
              const y = chartHeightPx - barHeight + 20;
              
              ctx.fillStyle = chartData.datasets[0].backgroundColor;
              ctx.fillRect(x, y, barWidth - 2, barHeight);
            });

            // Draw labels
            ctx.fillStyle = '#333';
            ctx.font = '12px Arial';
            chartData.labels.forEach((label, index) => {
              const x = 20 + index * barWidth;
              ctx.fillText(label, x, chartHeightPx + 35);
            });
          }

          // Convert to image and add to PDF
          const canvasElement = await html2canvas(tempDiv, {
            backgroundColor: 'white',
            scale: 1,
            useCORS: true
          });

          const imgData = canvasElement.toDataURL('image/png');
          pdf.addImage(imgData, 'PNG', margin, yPosition, chartWidth, chartHeight);

          // Clean up
          document.body.removeChild(tempDiv);
          
          yPosition += chartHeight + 20;
        }
      }

      // Open PDF in new tab
      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, '_blank');
      
      // Clean up the URL after a delay to free memory
      setTimeout(() => {
        URL.revokeObjectURL(pdfUrl);
      }, 1000);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF report. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Reporting Center</h2>
              <p className="text-sm text-gray-600">Comprehensive Reports & Analytics Dashboard</p>
            </div>
          </div>
          <div className="flex items-center space-x-12">
            {/* Report Dropdown */}
            <div className="flex flex-col space-y-2">
              <div className="flex items-center space-x-2">
                <FileText className="w-4 h-4 text-gray-500" />
                <span className="text-xs text-gray-500 font-medium">Report</span>
              </div>
              <div className="relative" ref={reportDropdownRef}>
                <button
                  onClick={() => setIsReportDropdownOpen(!isReportDropdownOpen)}
                  className="flex items-center justify-between bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-sm font-medium text-gray-700 hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 cursor-pointer min-w-[140px] shadow-sm"
                >
                  <span>{reportOptions.find(option => option.value === selectedReport)?.label}</span>
                  <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isReportDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isReportDropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl z-50 overflow-hidden">
                    {reportOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setSelectedReport(option.value);
                          setIsReportDropdownOpen(false);
                        }}
                        className={`w-full px-4 py-3 text-left text-sm font-medium transition-colors duration-150 ${
                          selectedReport === option.value
                            ? 'bg-primary-50 text-primary-700 border-l-4 border-primary-500'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Property Dropdown */}
            <div className="flex flex-col space-y-2">
              <div className="flex items-center space-x-2">
                <Bed className="w-4 h-4 text-gray-500" />
                <span className="text-xs text-gray-500 font-medium">Property</span>
              </div>
              <div className="relative" ref={propertyDropdownRef}>
                <button
                  onClick={() => setIsPropertyDropdownOpen(!isPropertyDropdownOpen)}
                  className="flex items-center justify-between bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-sm font-medium text-gray-700 hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 cursor-pointer min-w-[160px] shadow-sm"
                >
                  <span>{propertyOptions.find(option => option.value === selectedProperty)?.label}</span>
                  <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isPropertyDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isPropertyDropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl z-50 overflow-hidden">
                    {propertyOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setSelectedProperty(option.value);
                          setIsPropertyDropdownOpen(false);
                        }}
                        className={`w-full px-4 py-3 text-left text-sm font-medium transition-colors duration-150 ${
                          selectedProperty === option.value
                            ? 'bg-primary-50 text-primary-700 border-l-4 border-primary-500'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Time Period Dropdown */}
            <div className="flex flex-col space-y-2">
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <span className="text-xs text-gray-500 font-medium">Time Period</span>
              </div>
              <div className="relative" ref={periodDropdownRef}>
                <button
                  onClick={() => setIsPeriodDropdownOpen(!isPeriodDropdownOpen)}
                  className="flex items-center justify-between bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-sm font-medium text-gray-700 hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 cursor-pointer min-w-[160px] shadow-sm"
                >
                  <span>{periodOptions.find(option => option.value === selectedPeriod)?.label}</span>
                  <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isPeriodDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isPeriodDropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl z-50 overflow-hidden">
                    {periodOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setSelectedPeriod(option.value);
                          setIsPeriodDropdownOpen(false);
                        }}
                        className={`w-full px-4 py-3 text-left text-sm font-medium transition-colors duration-150 ${
                          selectedPeriod === option.value
                            ? 'bg-primary-50 text-primary-700 border-l-4 border-primary-500'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button 
              onClick={handleDownloadReport}
              disabled={isLoading}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors border border-blue-600 shadow-sm"
            >
              <Download className="w-4 h-4" />
              <span>Download Report</span>
            </button>
            <button 
              onClick={handleRefresh}
              disabled={isLoading}
              className="flex items-center space-x-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors border border-gray-300"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Revenue Breakdown - Stacked Bar Chart */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className={`p-1.5 rounded-full ${selectedReport === 'occupancy' ? 'bg-blue-100' : 'bg-green-100'}`}>
              {selectedReport === 'occupancy' ? (
                <Target className="w-5 h-5 text-blue-600" />
              ) : (
                <IndianRupee className="w-5 h-5 text-green-600" />
              )}
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              {selectedReport === 'occupancy' ? 'Room Occupancy' : 'Revenue Breakdown'}
            </h3>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-gray-900">
              {selectedReport === 'occupancy' 
                ? `${Math.round((occupancyData as any[]).reduce((sum: number, item: any) => sum + (item.roomOccupancy || 0), 0) / (occupancyData as any[]).length)}%`
                : formatCurrency((revenueData as any[]).reduce((sum: number, item: any) => sum + (item.totalRevenue || 0), 0))
              }
            </div>
            <div className="text-sm text-gray-600">
              {selectedPeriod === 'week' ? 'This Week' : 
               selectedPeriod === 'month' ? 'This Month' :
               selectedPeriod === 'quarter' ? 'This Quarter' : 'This Year'}
            </div>
          </div>
        </div>
        
        {/* Chart.js Stacked Bar Chart */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="h-96">
            <Bar data={chartData} options={chartOptions} />
          </div>
        </div>
      </div>

    </div>
  );
};

export default ReportingCenter;
